// ========================================
// Nóminas: «Subir nóminas» (control del mes), «Listado por mes» (el curso por persona)
// and «Gastos del curso» (coste empresa real). Payslips are read from the history
// (payroll-history.json); costs come from the server (/api/payroll/costs).
// ========================================

const NominasUpload = {
    HISTORY_KEY: 'ecpv_payroll_history',
    activeSection: 'upload', // 'upload' | 'listado' | 'gastos'
    MONTH_NAMES: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],

    // «Subir nóminas»: the month being worked on ({ month: 1-12, year }) and the table search.
    uploadMonth: null,
    uploadSearch: '',

    // «Listado por mes»: one course, by person.
    listadoYear: null,
    listadoSearch: '',
    listadoPay: 'all', // 'all' | 'hourly' | 'fixed'
    listadoOnlyMissing: false,

    // «Gastos del curso»
    costYearStart: null,
    costData: null,
    costScope: 'all', // 'all' | 'formation' | 'program'
    costFormationId: null,
    costProgramKey: null,

    render() {
        if (!this.canManage() && this.activeSection === 'upload') this.activeSection = 'listado';
        if (this.costYearStart === null) this.costYearStart = this.currentCourse();

        return `
            <div class="view-container dnr-proposal payroll-view">
                <div class="payroll-tabs-row">
                    <button class="btn btn-secondary professors-back-btn payroll-back-btn" ${UIEvents.attr("click", function(event,captured){App.navigate('profesores');}, [])} title="Volver a Profesores">
                        <i class="fas fa-arrow-left"></i>
                    </button>
                    <div class="payroll-tabs">
                        ${this.renderSectionTabs()}
                    </div>
                </div>

                <div id="payroll-section-content"></div>
            </div>

            <!-- Modal Preview -->
            <div id="payroll-modal" class="modal-overlay" style="display: none; position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.85); z-index: 9999; backdrop-filter: blur(4px); align-items: center; justify-content: center; opacity: 0; transition: opacity 0.3s ease;">
                <div class="modal-container" style="width: 90%; max-width: 1000px; height: 90%; background: var(--bg-primary); border-radius: 16px; position: relative; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 50px rgba(0,0,0,0.5); transform: scale(0.9); transition: transform 0.3s ease;">
                    <div class="modal-header" style="padding: 1rem 1.5rem; background: var(--bg-secondary); border-bottom: 1px solid var(--border-color); display: flex; justify-content: space-between; align-items: center;">
                        <h3 id="modal-doc-title" style="margin: 0; font-size: 1.1rem; color: var(--text-primary);">Vista Previa</h3>
                        <div style="display: flex; gap: 0.75rem;">
                            <button id="modal-download-btn" class="btn btn-sm btn-primary" style="padding: 0.5rem 1rem;">
                                <i class="fas fa-download"></i> Descargar
                            </button>
                            <button ${UIEvents.attr("click", function(event,captured){NominasUpload.closeModal();}, [])} class="btn-icon" style="color: var(--text-secondary); font-size: 1.25rem;">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                    <div id="modal-doc-content" style="flex: 1; display: flex; align-items: center; justify-content: center; background: #e2e8f0; overflow: auto; padding: 1rem;">
                        <!-- Content injected here -->
                    </div>
                </div>
            </div>
        `;
    },

    init() {
        this.renderSection();

        // Modal events
        window.onclick = (e) => {
            const modal = document.getElementById('payroll-modal');
            if (e.target === modal) this.closeModal();
        };
    },

    // ========================================
    // Section Tabs (Subir / Listado / Gastos)
    // ========================================

    renderSectionTabs() {
        const sections = [
            { key: 'upload', label: 'Subir Nóminas', icon: 'fa-cloud-upload-alt' },
            { key: 'listado', label: 'Listado por Mes', icon: 'fa-list-ul' },
            { key: 'gastos', label: 'Gastos del Curso', icon: 'fa-chart-line' }
        ];
        return sections.filter(s => s.key !== 'upload' || this.canManage()).map(s => {
            const isActive = this.activeSection === s.key;
            return `
                <button class="payroll-tab ${isActive ? 'active' : ''}" ${UIEvents.attr("click", function(event,captured){NominasUpload.switchSection(String(captured[0]));}, [s.key])}>
                    <i class="fas ${s.icon}"></i> ${Utils.sanitizeHTML(s.label)}
                </button>
            `;
        }).join('');
    },

    switchSection(key) {
        if (this.activeSection === key) return;
        this.activeSection = key;

        const tabsContainer = document.querySelector('.payroll-tabs');
        if (tabsContainer) SafeHTML.set(tabsContainer, this.renderSectionTabs());

        this.renderSection();
    },

    renderSection() {
        const content = document.getElementById('payroll-section-content');
        if (!content) return;

        if (this.activeSection === 'listado') {
            SafeHTML.set(content, this.renderListadoSectionHTML());
        } else if (this.activeSection === 'gastos') {
            SafeHTML.set(content, this.renderGastosSectionHTML());
            this.loadAndRenderCosts();
        } else {
            SafeHTML.set(content, this.renderUploadSectionHTML());
            this.setupDropZone();
        }
    },

    // ========================================
    // Shared: months, courses and who a payslip is due from
    // ========================================

    canManage() {
        return AuthManager.canManageModule('nominas', ['admin', 'rrhh']);
    },

    /** Start year of the course a month belongs to (a course runs September to August). */
    courseOf(month, year) {
        return month >= 9 ? year : year - 1;
    },

    currentCourse() {
        const now = new Date();
        return this.courseOf(now.getMonth() + 1, now.getFullYear());
    },

    /** The twelve months of a course, September first. */
    courseMonths(yearStart) {
        return [9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8].map(month => ({ month, year: month >= 9 ? yearStart : yearStart + 1 }));
    },

    /** -1 past, 0 this month, 1 future. */
    monthWhen(month, year) {
        const now = new Date(), a = year * 12 + month, b = now.getFullYear() * 12 + now.getMonth() + 1;
        return a < b ? -1 : a === b ? 0 : 1;
    },

    /** A month's payslips become due on its 28th (the school's rule, 2026-10-25); earlier ones always are. */
    DUE_DAY: 28,
    isDue(month, year) {
        const when = this.monthWhen(month, year);
        return when < 0 || (when === 0 && new Date().getDate() >= this.DUE_DAY);
    },

    /** Loads a whole course of classes (every formation) and redraws when it arrives. */
    needCourse(yearStart, onLoaded) {
        if (typeof FileStorage === 'undefined' || !FileStorage.needClasses) return true;
        return FileStorage.needClasses(`${yearStart}-09-01`, `${yearStart + 1}-08-31`, onLoaded);
    },

    /** People a payslip is due from in a month: classes that month, or a contract covering it. */
    dueIds(month, year) {
        const monthPrefix = `${year}-${String(month).padStart(2, '0')}`;
        const ids = new Set();
        for (const c of DataManager.getClasses() || []) {
            if (!c.startDate || !c.startDate.startsWith(monthPrefix)) continue;
            const profId = c.professorId || (c.subjectId && DataManager.getSubject ? DataManager.getSubject(c.subjectId)?.professorId : null);
            if (profId) ids.add(profId);
        }
        // Any of their contracts covering some day of the month (people can be hired several times).
        for (const p of DataManager.getProfessors() || []) {
            if (StaffPay.contractsOf(p).length && StaffPay.employedInMonth(p, year, month - 1)) ids.add(p.id);
        }
        return ids;
    },

    /** Payslips of one month. */
    payslipsOf(month, year, history = this.getHistory()) {
        return history.filter(h => Number(h.monthIndex) === Number(month) && String(h.year) === String(year));
    },

    personName(id, fallback = '') {
        const p = DataManager.getProfessor(id);
        return p ? `${p.firstName} ${p.lastName}`.trim() : fallback;
    },

    // Foto de la ficha o, si no tiene, sus iniciales.
    avatarHTML(id, name) {
        const p = DataManager.getProfessor(id);
        if (p && p.photo) return `<span class="pn-avatar"><img ${StudentPhotos.attributes(p.photo)} alt=""></span>`;
        const initials = String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
        return `<span class="pn-avatar" aria-hidden="true">${Utils.sanitizeHTML(initials)}</span>`;
    },

    /** The person's name, opening their ficha when they have one. */
    personNameHTML(id, name) {
        const text = Utils.sanitizeHTML(name || 'Desconocido');
        if (!id || !DataManager.getProfessor(id)) return `<span class="pn-person-name">${text}</span>`;
        return `<button type="button" class="pn-person-name pn-person-link" title="Abrir su ficha" ${UIEvents.attr("click", function(event,captured){App.navigate('profesor/' + String(captured[0]));}, [id])}>${text}</button>`;
    },

    payLabel(id) {
        const p = DataManager.getProfessor(id);
        if (!p) return '';
        if (!StaffPay.isFixed(p)) return StaffPay.isFreelance(p) ? 'Autónomo' : 'Por horas';
        return 'Sueldo fijo';
    },

    /** Whether that person's month is an invoice (a freelance period) rather than a payslip. */
    isInvoiceSlot(professorId, month, year) {
        const p = DataManager.getProfessor(professorId);
        return !!p && StaffPay.freelanceInMonth(p, Number(year), Number(month) - 1);
    },

    money(n) {
        return Utils.formatCurrency(n);
    },

    SETTLEMENT_FIELDS: ['settlementFileName', 'settlementFilePath', 'settlementDate', 'settlementCost', 'settlementNet'],

    /**
     * What a month's payslip adds up to with its settlement (finiquito): the «Suma» is company
     * cost and also gross; its net adds to the net. Company cost stays unknown without the payslip's.
     */
    totalOf(h, key) {
        const extra = !h.settlementFilePath ? 0 : key === 'netPay' ? Number(h.settlementNet) || 0 : Number(h.settlementCost) || 0;
        if (h[key] == null) return key === 'companyCost' ? null : (extra || null);
        return Number(h[key]) + extra;
    },

    // Obtiene los profesores activos en un mes y año concretos (con clases programadas o contrato en vigor)
    // EXCLUYENDO aquellos cuya nómina ya ha sido subida para ese mes y año
    getActiveProfessorsForMonth(month, year) {
        const allProfessors = DataManager.getProfessors() || [];
        const monthNum = parseInt(month, 10), yearNum = parseInt(year, 10);
        if (!monthNum || !yearNum || !allProfessors.length) return allProfessors;
        const monthPrefix = `${yearNum}-${String(monthNum).padStart(2, '0')}`;
        // A month of another academic year is loaded on demand, then the list is rebuilt.
        if (typeof FileStorage !== 'undefined' && FileStorage.needClasses) {
            FileStorage.needClasses(`${monthPrefix}-01`, `${monthPrefix}-${String(new Date(yearNum, monthNum, 0).getDate()).padStart(2, '0')}`, () => this.updateProfessorSelect());
        }
        const due = this.dueIds(monthNum, yearNum);
        const uploaded = new Set(this.payslipsOf(monthNum, yearNum).map(h => h.professorId));
        return allProfessors
            .filter(p => due.has(p.id) && !uploaded.has(p.id))
            .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
    },

    // Actualiza dinámicamente el selector de profesores según el mes y año seleccionados
    updateProfessorSelect(preferredProfId = null) {
        const form = document.getElementById('nominas-upload-form');
        if (!form) return;

        const monthSelect = form.querySelector('select[name="month"]');
        const yearSelect = form.querySelector('select[name="year"]');
        const profSelect = form.querySelector('select[name="professorId"]');
        if (!monthSelect || !yearSelect || !profSelect) return;

        const currentVal = preferredProfId !== null ? preferredProfId : profSelect.value;
        const pendingProfessors = this.getActiveProfessorsForMonth(parseInt(monthSelect.value, 10), parseInt(yearSelect.value, 10));
        // Someone chosen on purpose (a «Falta» cell) stays selectable even when not on the list.
        const chosen = currentVal && !pendingProfessors.some(p => p.id === currentVal) ? DataManager.getProfessor(currentVal) : null;
        const options = chosen ? [chosen, ...pendingProfessors] : pendingProfessors;

        let html = '';
        if (options.length === 0) {
            html = '<option value="" disabled selected>Todas las nóminas de este mes ya han sido subidas</option>';
        } else {
            html = `<option value="">Elige a la persona… (${pendingProfessors.length} pendiente${pendingProfessors.length === 1 ? '' : 's'})</option>`;
            options.forEach(p => {
                html += `<option value="${Utils.sanitizeHTML(p.id)}" ${p.id === currentVal ? 'selected' : ''}>${Utils.sanitizeHTML(p.firstName)} ${Utils.sanitizeHTML(p.lastName)}</option>`;
            });
        }
        SafeHTML.set(profSelect, html);
        profSelect.value = currentVal && options.some(p => p.id === currentVal) ? currentVal : '';
    },

    // ========================================
    // SECCIÓN: Subir Nóminas (control del mes)
    // ========================================

    /** The month on screen: the one chosen, else the latest with payslips, else this month. */
    selectedUploadMonth() {
        if (!this.uploadMonth) {
            const latest = this.getHistory().slice().sort((a, b) => (Number(b.year) * 12 + Number(b.monthIndex)) - (Number(a.year) * 12 + Number(a.monthIndex)))[0];
            const now = new Date();
            this.uploadMonth = latest ? { month: Number(latest.monthIndex), year: Number(latest.year) } : { month: now.getMonth() + 1, year: now.getFullYear() };
        }
        return this.uploadMonth;
    },

    selectUploadMonth(month, year) {
        this.uploadMonth = { month: Number(month), year: Number(year) };
        this.uploadSearch = '';
        this.renderSection();
    },

    renderUploadSectionHTML() {
        const { month, year } = this.selectedUploadMonth();
        const course = this.courseOf(month, year);
        this.needCourse(course, () => { if (this.activeSection === 'upload') this.renderSection(); });
        const history = this.getHistory();
        const monthName = this.MONTH_NAMES[month - 1];
        const payslips = this.payslipsOf(month, year, history);
        const uploadedIds = new Set(payslips.map(h => h.professorId));
        const due = this.dueIds(month, year);
        const pending = [...due].filter(id => !uploadedIds.has(id)).map(id => ({ id, name: this.personName(id) }))
            .filter(p => p.name).sort((a, b) => a.name.localeCompare(b.name));
        const expected = new Set([...due, ...uploadedIds]).size;
        const sum = key => payslips.reduce((s, h) => s + (this.totalOf(h, key) || 0), 0);
        const withoutCost = payslips.filter(h => h.companyCost == null).length;
        const readable = payslips.filter(h => h.companyCost == null && h.filePath && String(h.fileType).includes('pdf')).length;
        const settlements = payslips.filter(h => h.settlementFilePath).length;
        const thisYear = new Date().getFullYear();
        const stats = [
            { label: 'Subidas', value: `${payslips.length} / ${expected}`, sub: pending.length ? `Faltan ${pending.length} ${pending.length === 1 ? 'persona' : 'personas'}` : 'Están todas' },
            { label: 'Coste empresa', value: this.money(sum('companyCost')), sub: withoutCost ? `${withoutCost} sin importe` : settlements ? `Con ${settlements} ${settlements === 1 ? 'finiquito' : 'finiquitos'}` : 'Lo que paga la escuela', warn: !!withoutCost },
            { label: 'Bruto', value: this.money(sum('grossPay')), sub: 'Total devengado' },
            { label: 'Líquido', value: this.money(sum('netPay')), sub: 'Lo que cobran' }
        ];

        return `
            <div class="pn-stack">
                ${this.renderMonthStrip(course, history)}
                <div class="pn-upload-layout">
                    <aside class="pn-aside">
                        <section class="pn-card pn-pad">
                            <h2 class="pn-title">Subir nóminas</h2>
                            <div class="pn-pair">
                                <label class="pn-field">Mes
                                    <select class="form-input" ${UIEvents.attr("change", function(event,captured){NominasUpload.selectUploadMonth(this.value, NominasUpload.selectedUploadMonth().year);}, [])}>
                                        ${this.MONTH_NAMES.map((m, i) => `<option value="${i + 1}" ${i + 1 === month ? 'selected' : ''}>${m}</option>`).join('')}
                                    </select>
                                </label>
                                <label class="pn-field">Año
                                    <select class="form-input" ${UIEvents.attr("change", function(event,captured){NominasUpload.selectUploadMonth(NominasUpload.selectedUploadMonth().month, this.value);}, [])}>
                                        ${[...new Set([thisYear - 1, thisYear, thisYear + 1, year])].sort().map(y => `<option value="${y}" ${y === year ? 'selected' : ''}>${y}</option>`).join('')}
                                    </select>
                                </label>
                            </div>
                            <label class="pn-drop" id="pn-drop-zone">
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M12 17v-6"/><path d="m9.5 13.5 2.5-2.5 2.5 2.5"/></svg>
                                <span class="pn-drop-title">Suelta aquí el PDF de ${Utils.sanitizeHTML(monthName.toLowerCase())}</span>
                                <span class="pn-drop-text">Una nómina, todas juntas o el PDF de finiquitos. SICAF separa las páginas, reconoce a cada persona por su DNI y lee los importes. Cada finiquito se une a la nómina de su mes.</span>
                                <span class="pn-drop-btn">Elegir PDF</span>
                                <input type="file" id="pn-drop-input" accept="application/pdf,.pdf" aria-label="PDF con las nóminas de ${Utils.sanitizeHTML(monthName)} ${year}" hidden>
                            </label>
                            <p class="pn-hint" id="pn-drop-status" role="status">Antes de guardar nada verás la lista para revisarla. ¿Es una imagen escaneada? <button type="button" class="pn-link" ${UIEvents.attr("click", function(event,captured){NominasUpload.openManualUpload();}, [])}>Súbela a mano</button>.</p>
                        </section>

                        <section class="pn-card pn-pad" aria-labelledby="pn-pending-title">
                            <div class="pn-row-between">
                                <h2 class="pn-title pn-title-sm" id="pn-pending-title">${pending.length ? `Faltan ${pending.length}` : 'Están todas'}</h2>
                                <span class="pn-mono-note">${Utils.sanitizeHTML(monthName)}</span>
                            </div>
                            ${pending.length ? `<ul class="pn-pending">
                                ${pending.map(p => `<li>
                                    <button type="button" class="pn-pending-item" title="${this.isInvoiceSlot(p.id, month, year) ? 'Subir su factura a mano' : 'Subir su nómina a mano'}" ${UIEvents.attr("click", function(event,captured){NominasUpload.openManualUpload(String(captured[0]));}, [p.id])}>
                                        <span>${Utils.sanitizeHTML(p.name)}</span>
                                        <span class="pn-tag">${Utils.sanitizeHTML(this.payLabel(p.id))}</span>
                                    </button>
                                </li>`).join('')}
                            </ul>` : `<p class="pn-hint">Todas las personas con clases o contrato en ${Utils.sanitizeHTML(monthName.toLowerCase())} tienen su nómina o su factura.</p>`}
                        </section>
                    </aside>

                    <div class="pn-main">
                        <div class="pn-stats">
                            ${stats.map(s => `<div class="pn-stat">
                                <span class="pn-stat-label">${s.label}</span>
                                <span class="pn-stat-value">${Utils.sanitizeHTML(s.value)}</span>
                                <span class="pn-stat-sub ${s.warn ? 'is-warn' : ''}">${Utils.sanitizeHTML(s.sub)}</span>
                            </div>`).join('')}
                        </div>
                        ${withoutCost ? `<div class="payroll-pending-banner payroll-amounts-banner">
                            <i class="fas fa-euro-sign" aria-hidden="true"></i>
                            <span class="ppb-text"><strong>${withoutCost} ${withoutCost === 1 ? 'nómina' : 'nóminas'} de ${Utils.sanitizeHTML(monthName.toLowerCase())} sin coste empresa.</strong> Sin él no cuentan en «Gastos del curso».</span>
                            ${readable ? `<button type="button" class="btn btn-sm btn-primary" ${UIEvents.attr("click", function(event,captured){NominasUpload.readStoredAmounts(this);}, [])}><i class="fas fa-magnifying-glass"></i> Leer importes de los PDF</button>` : ''}
                        </div>` : ''}
                        <section class="pn-card pn-table-card" aria-labelledby="pn-month-title">
                            <div class="pn-table-head">
                                <h2 class="pn-title" id="pn-month-title">${Utils.sanitizeHTML(monthName)} ${year} · ${payslips.length} ${payslips.length === 1 ? 'nómina' : 'nóminas'}</h2>
                                ${payslips.length ? `<label class="pn-search">
                                    <i class="fas fa-search" aria-hidden="true"></i>
                                    <input type="search" placeholder="Buscar persona" aria-label="Buscar persona" value="${Utils.sanitizeHTML(this.uploadSearch)}" ${UIEvents.attr("input", function(event,captured){NominasUpload.uploadSearch=this.value;NominasUpload.renderUploadRows();}, [])}>
                                </label>` : ''}
                            </div>
                            <div class="pn-scroll" id="pn-upload-rows">${this.uploadRowsHTML(payslips, monthName)}</div>
                        </section>
                    </div>
                </div>
            </div>
        `;
    },

    /** The course's twelve months, each with «uploaded / due» and a progress bar. */
    renderMonthStrip(course, history) {
        const selected = this.selectedUploadMonth();
        const chips = this.courseMonths(course).map(({ month, year }) => {
            const when = this.monthWhen(month, year);
            const uploaded = new Set(this.payslipsOf(month, year, history).map(h => h.professorId));
            const expected = new Set([...(this.isDue(month, year) ? this.dueIds(month, year) : []), ...uploaded]).size;
            const isSelected = month === selected.month && year === selected.year;
            const state = isSelected ? 'is-active' : when === 0 ? 'is-current' : when > 0 && !uploaded.size ? 'is-future' : '';
            const pct = expected ? Math.round(uploaded.size / expected * 100) : 0;
            const name = this.MONTH_NAMES[month - 1];
            return `<button type="button" class="pn-month ${state}" aria-pressed="${isSelected}" aria-label="${name} ${year}: ${uploaded.size} de ${expected} nóminas" ${UIEvents.attr("click", function(event,captured){NominasUpload.selectUploadMonth(Number(captured[0]),Number(captured[1]));}, [month, year])}>
                <span class="pn-month-label">${name.slice(0, 3)}</span>
                <span class="pn-month-count">${expected ? `${uploaded.size} / ${expected}` : '—'}</span>
                <span class="pn-month-track"><span class="pn-month-bar" style="width: ${pct}%"></span></span>
            </button>`;
        }).join('');
        return `<section class="pn-card pn-months" aria-label="Meses del curso">
            <div class="pn-row-between">
                <span class="pn-mono-note">Curso ${course} / ${course + 1}</span>
                <span class="pn-hint">Cada mes: nóminas subidas de las que tocan</span>
            </div>
            <div class="pn-month-grid">${chips}</div>
        </section>`;
    },

    renderUploadRows() {
        const box = document.getElementById('pn-upload-rows');
        if (!box) return;
        const { month, year } = this.selectedUploadMonth();
        SafeHTML.set(box, this.uploadRowsHTML(this.payslipsOf(month, year), this.MONTH_NAMES[month - 1]));
    },

    uploadRowsHTML(payslips, monthName) {
        if (!payslips.length) {
            return `<div class="dnr-empty pn-empty">Aún no hay nóminas de ${Utils.sanitizeHTML(monthName.toLowerCase())}. Suelta el PDF del mes en «Subir nóminas».</div>`;
        }
        const term = this.normalizeText(this.uploadSearch);
        const name = h => this.personName(h.professorId, h.professorName || 'Desconocido');
        const rows = payslips.filter(h => !term || this.normalizeText(name(h)).includes(term)).sort((a, b) => name(a).localeCompare(name(b)));
        const sum = key => payslips.reduce((s, h) => s + (this.totalOf(h, key) || 0), 0);
        const withoutCost = payslips.filter(h => h.companyCost == null).length;
        const amount = (value, strong) => value == null ? (strong ? '<span class="pn-missing">Sin importe</span>' : '—') : this.money(value);
        // The settlement's part under the payslip's amount (short: the person is already tagged «Finiquito»).
        const plus = (h, value) => h.settlementFilePath ? `<span class="pn-settle" title="Finiquito">+ ${value == null ? '—' : this.money(value)}<span class="sr-only"> de finiquito</span></span>` : '';
        const manage = this.canManage();
        const when = ts => { const d = new Date(ts); return Number.isNaN(d.getTime()) ? '' : `${d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}<br><span class="pn-faint">${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>`; };
        return `<table class="pn-table pn-table-month">
            <thead><tr>
                <th scope="col">Persona</th>
                <th scope="col" class="pn-num">Coste empresa</th>
                <th scope="col" class="pn-num">Bruto</th>
                <th scope="col" class="pn-num">Líquido</th>
                <th scope="col" class="pn-when">Subida</th>
                <th scope="col" class="pn-num"><span class="sr-only">Acciones</span></th>
            </tr></thead>
            <tbody>
                ${rows.length ? rows.map(h => `<tr>
                    <td><div class="pn-person">
                        ${this.avatarHTML(h.professorId, name(h))}
                        <span class="pn-person-text">${this.personNameHTML(h.professorId, name(h))}<span class="pn-person-kind">${Utils.sanitizeHTML(this.payLabel(h.professorId))}${h.isInvoice ? ' · <span class="pn-settle-tag" title="Coste empresa = total de la factura; bruto = base imponible; líquido = lo que se le paga">Factura</span>' : ''}${h.settlementFilePath ? ' · <span class="pn-settle-tag">Finiquito</span>' : ''}</span></span>
                    </div></td>
                    <td class="pn-num pn-cost">${amount(h.companyCost, true)}${plus(h, h.settlementCost)}</td>
                    <td class="pn-num pn-soft">${amount(h.grossPay)}${plus(h, h.settlementCost)}</td>
                    <td class="pn-num pn-soft">${amount(h.netPay)}${plus(h, h.settlementNet)}</td>
                    <td class="pn-when">${when(h.timestamp)}</td>
                    <td class="pn-num">${this.rowActionsHTML(h, manage)}</td>
                </tr>`).join('') : `<tr><td colspan="6"><div class="dnr-empty pn-empty">Nadie coincide con «${Utils.sanitizeHTML(this.uploadSearch)}».</div></td></tr>`}
            </tbody>
            <tfoot><tr>
                <th scope="row">Total del mes</th>
                <td class="pn-num">${this.money(sum('companyCost'))}</td>
                <td class="pn-num">${this.money(sum('grossPay'))}</td>
                <td class="pn-num">${this.money(sum('netPay'))}</td>
                <td colspan="2" class="pn-foot-note">${withoutCost ? `${withoutCost} ${withoutCost === 1 ? 'nómina' : 'nóminas'} sin coste empresa` : ''}</td>
            </tr></tfoot>
        </table>`;
    },

    rowActionsHTML(h, manage) {
        return `<div class="pn-actions">
            <button type="button" class="pn-icon-btn" aria-label="Ver nómina" title="Ver" ${UIEvents.attr("click", function(event,captured){NominasUpload.viewDocument(String(captured[0]));}, [h.timestamp])}><i class="fas fa-eye"></i></button>
            ${manage ? `<button type="button" class="pn-icon-btn" aria-label="Editar importes" title="Importes" ${UIEvents.attr("click", function(event,captured){NominasUpload.editAmounts(String(captured[0]));}, [h.timestamp])}><i class="fas fa-pen"></i></button>` : ''}
            <details class="pn-more">
                <summary class="pn-icon-btn" aria-label="Más acciones" title="Más"><i class="fas fa-ellipsis"></i></summary>
                <div class="pn-more-menu">
                    <button type="button" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;NominasUpload.downloadDocument(String(captured[0]));}, [h.timestamp])}><i class="fas fa-download"></i> Descargar</button>
                    ${h.settlementFilePath ? `<button type="button" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;NominasUpload.viewDocument(String(captured[0]),true);}, [h.timestamp])}><i class="fas fa-file-signature"></i> Ver finiquito</button>
                    <button type="button" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;NominasUpload.downloadDocument(String(captured[0]),true);}, [h.timestamp])}><i class="fas fa-download"></i> Descargar finiquito</button>
                    ${manage ? `<button type="button" class="is-danger" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;NominasUpload.deleteSettlement(String(captured[0]));}, [h.timestamp])}><i class="fas fa-file-circle-xmark"></i> Quitar finiquito</button>` : ''}` : ''}
                    ${manage ? `<button type="button" class="is-danger" ${UIEvents.attr("click", function(event,captured){this.closest('details').open=false;NominasUpload.deleteDocument(String(captured[0]));}, [h.timestamp])}><i class="fas fa-trash-alt"></i> Eliminar</button>` : ''}
                </div>
            </details>
        </div>`;
    },

    /** The drop zone: a chosen or dropped PDF goes straight to the review (NominasBatch). */
    setupDropZone() {
        const zone = document.getElementById('pn-drop-zone'), input = document.getElementById('pn-drop-input');
        if (!zone || !input) return;
        const take = file => {
            if (!file) return;
            const { month, year } = this.selectedUploadMonth();
            NominasBatch.analyzeFile(file, month, year, document.getElementById('pn-drop-status'));
        };
        input.onchange = () => { take(input.files[0]); input.value = ''; };
        zone.ondragover = e => { e.preventDefault(); zone.classList.add('is-over'); };
        zone.ondragleave = () => zone.classList.remove('is-over');
        zone.ondrop = e => { e.preventDefault(); zone.classList.remove('is-over'); take(e.dataTransfer.files[0]); };
        // An open «more» menu closes when clicking anywhere else.
        if (!this._menuCloser) {
            this._menuCloser = event => document.querySelectorAll('.pn-more[open]').forEach(menu => { if (!menu.contains(event.target)) menu.open = false; });
            document.addEventListener('click', this._menuCloser);
        }
    },

    /** One payslip by hand (a scanned image, or someone the PDF did not cover). */
    openManualUpload(professorId = null, month = null, year = null) {
        if (!AuthManager.requireModuleManage('nominas', ['admin', 'rrhh'])) return;
        const selected = this.selectedUploadMonth();
        month = Number(month || selected.month);
        year = Number(year || selected.year);
        const thisYear = new Date().getFullYear();
        ModalManager.show('Subir una nómina o factura a mano', `
            <div class="dnr-proposal professor-form-dnr">
            <form id="nominas-upload-form" class="form-container" ${UIEvents.attr("submit", function(event,captured){NominasUpload.handleUpload(event);}, [])}>
                <div class="form-grid">
                    <div class="form-group" style="grid-column: 1 / -1;">
                        <label class="form-label" for="payroll-professor-select">Persona</label>
                        <select class="form-input" name="professorId" id="payroll-professor-select" required ${UIEvents.attr("change", function(event,captured){NominasUpload.applyInvoiceMode();}, [])}></select>
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="payroll-month-select">Mes</label>
                        <select class="form-input" name="month" id="payroll-month-select" ${UIEvents.attr("change", function(event,captured){NominasUpload.updateProfessorSelect();NominasUpload.applyInvoiceMode();}, [])} required>
                            ${this.MONTH_NAMES.map((m, i) => `<option value="${i + 1}" ${i + 1 === month ? 'selected' : ''}>${m}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="payroll-year-select">Año</label>
                        <select class="form-input" name="year" id="payroll-year-select" ${UIEvents.attr("change", function(event,captured){NominasUpload.updateProfessorSelect();NominasUpload.applyInvoiceMode();}, [])} required>
                            ${[...new Set([thisYear - 1, thisYear, thisYear + 1, year])].sort().map(y => `<option value="${y}" ${y === year ? 'selected' : ''}>${y}</option>`).join('')}
                        </select>
                    </div>
                    <div class="form-group" style="grid-column: 1 / -1;">
                        <label class="form-label" for="payroll-file-input" id="payroll-file-label">Nómina (PDF o imagen)</label>
                        <input type="file" class="form-input" name="payrollFile" id="payroll-file-input" accept=".pdf,image/*" required>
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="payroll-company-cost" id="payroll-cost-label">Coste empresa (€)</label>
                        <input type="number" class="form-input" name="companyCost" id="payroll-company-cost" min="0" step="0.01" inputmode="decimal" placeholder="Se lee del PDF">
                    </div>
                    <div class="form-group">
                        <label class="form-label" for="payroll-gross-pay" id="payroll-gross-label">Bruto (€)</label>
                        <input type="number" class="form-input" name="grossPay" id="payroll-gross-pay" min="0" step="0.01" inputmode="decimal" placeholder="Se lee del PDF">
                    </div>
                    <div class="form-group" id="payroll-withholding-group" hidden>
                        <label class="form-label" for="payroll-withholding">Retención de IRPF (€)</label>
                        <input type="number" class="form-input" name="withholding" id="payroll-withholding" min="0" step="0.01" inputmode="decimal" disabled>
                    </div>
                </div>
                <p class="pf-hint payroll-amounts-status" id="payroll-amounts-status" role="status"></p>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-upload"></i> Subir y notificar</button>
                </div>
            </form>
            </div>`);
        this.updateProfessorSelect(professorId || '');
        this.applyInvoiceMode();
        const fileInput = document.getElementById('payroll-file-input');
        if (fileInput) fileInput.onchange = () => { if (fileInput.files[0] && !this.manualIsInvoice()) this.readUploadAmounts(fileInput.files[0]); };
    },

    /** Whether the hand upload form is for an invoice: the chosen person is freelance that month. */
    manualIsInvoice() {
        const form = document.getElementById('nominas-upload-form');
        return !!form && !!form.elements.professorId.value && this.isInvoiceSlot(form.elements.professorId.value, form.elements.month.value, form.elements.year.value);
    },

    /** An autónomo's month takes an invoice: its total, tax base and IRPF withholding, typed by hand. */
    applyInvoiceMode() {
        const form = document.getElementById('nominas-upload-form');
        if (!form) return;
        const invoice = this.manualIsInvoice(), text = (id, value) => { const el = document.getElementById(id); if (el) el.textContent = value; };
        text('payroll-file-label', invoice ? 'Factura (PDF o imagen)' : 'Nómina (PDF o imagen)');
        text('payroll-cost-label', invoice ? 'Total de la factura (€)' : 'Coste empresa (€)');
        text('payroll-gross-label', invoice ? 'Base imponible (€)' : 'Bruto (€)');
        const group = document.getElementById('payroll-withholding-group');
        if (group) { group.hidden = !invoice; group.querySelector('input').disabled = !invoice; }
        for (const id of ['payroll-company-cost', 'payroll-gross-pay']) { const el = document.getElementById(id); if (el) el.placeholder = invoice ? 'Escríbelo de la factura' : 'Se lee del PDF'; }
        const status = document.getElementById('payroll-amounts-status'), note = 'Autónomo: escribe los importes de su factura. En «Gastos del curso» cuenta el total.';
        if (status && (invoice || status.textContent === note)) status.textContent = invoice ? note : '';
    },

    /** Fills «Coste empresa» and «Bruto» from the chosen PDF (an image is typed by hand). */
    async readUploadAmounts(file) {
        const read = this._amountsRead = (this._amountsRead || 0) + 1;
        const cost = document.getElementById('payroll-company-cost'), gross = document.getElementById('payroll-gross-pay');
        const status = document.getElementById('payroll-amounts-status');
        this._readNetPay = null;
        if (!cost || !gross || !status) return;
        cost.value = '';
        gross.value = '';
        if (!String(file.type).includes('pdf')) { status.textContent = 'Es una imagen: escribe el coste empresa y el bruto a mano.'; return; }
        status.textContent = 'Leyendo los importes de la nómina…';
        try {
            const amounts = await NominasBatch.readPdfAmounts(new Uint8Array(await file.arrayBuffer()));
            if (read !== this._amountsRead) return; // another file was chosen meanwhile
            cost.value = NominasBatch.amountValue(amounts.companyCost);
            gross.value = NominasBatch.amountValue(amounts.grossPay);
            this._readNetPay = amounts.netPay;
            status.textContent = amounts.companyCost != null ? 'Importes leídos de la nómina: revísalos antes de subir.' : 'No se ha encontrado el coste empresa en el PDF: escríbelo a mano.';
        } catch (e) {
            if (read === this._amountsRead) status.textContent = 'No se pudieron leer los importes: escríbelos a mano.';
        }
    },

    async handleUpload(event) {
        event.preventDefault();
        if (!AuthManager.requireModuleManage("nominas",["admin","rrhh"])) return;
        const form = event.target, button = form.querySelector('[type="submit"]');
        const data = Object.fromEntries(new FormData(form));
        const professor = DataManager.getProfessor(data.professorId);
        if (!professor) { Utils.showToast('Elige a la persona de la nómina.', 'error'); return; }
        const file = document.getElementById('payroll-file-input')?.files[0];
        if (!file) { Utils.showToast('Elige el archivo de la nómina.', 'error'); return; }
        if (button?.disabled) return;
        if (button) button.disabled = true;

        const month = parseInt(data.month, 10), monthName = this.MONTH_NAMES[month - 1];
        const isInvoice = this.isInvoiceSlot(data.professorId, month, data.year);
        const total = NominasBatch.parseAmount(data.companyCost), withholding = isInvoice ? NominasBatch.parseAmount(data.withholding) : null;
        if (isInvoice && total == null) { Utils.showToast('Escribe el total de la factura.', 'error'); return; }
        try {
            const safeFilename = `payroll_${data.professorId}_${data.year}_${data.month}_${Date.now()}${String(file.type).includes('pdf') ? '.pdf' : '.png'}`;
            Utils.showToast('Subiendo archivo...', 'info');
            const response = await fetch(`/upload?filename=${safeFilename}&folder=nominas`, {
                method: 'POST', headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: file
            });
            const result = await response.json().catch(() => null);
            if (!response.ok || !result?.success) throw new Error(result?.error || 'Error al subir');

            await this.saveUploads([{
                professorId: data.professorId,
                professorName: `${professor.firstName} ${professor.lastName}`,
                month: monthName,
                monthIndex: month,
                year: data.year,
                fileName: file.name,
                fileType: file.type,
                filePath: result.path,
                timestamp: new Date().toISOString(),
                companyCost: total,
                grossPay: NominasBatch.parseAmount(data.grossPay),
                // An invoice pays its total minus the IRPF withheld.
                netPay: isInvoice ? (withholding != null ? Math.round((total - withholding) * 100) / 100 : null) : this._readNetPay ?? null,
                isInvoice
            }]);
            this._readNetPay = null;
            this.notifyUploaded(data.professorId, monthName, data.year, false, isInvoice);
            ModalManager.closeModal();
            Utils.showToast(isInvoice ? 'Factura subida con éxito' : 'Nómina subida con éxito', 'success');
            this.uploadMonth = { month, year: Number(data.year) };
            this.renderSection();
        } catch (error) {
            console.error('Upload error:', error);
            Utils.showToast('Error al subir el archivo: ' + error.message, 'error');
            if (button) button.disabled = false;
        }
    },

    /**
     * Adds new payslips to the history in one write. A person has one payslip per month: a new
     * one replaces the earlier one (whose file is deleted first, while its record still exists).
     */
    async saveUploads(items) {
        const history = this.getHistory();
        const sameSlot = (a, b) => a.professorId === b.professorId && Number(a.monthIndex) === Number(b.monthIndex) && String(a.year) === String(b.year);
        const replaced = history.filter(old => items.some(item => sameSlot(old, item)));
        for (const old of replaced) {
            if (!old.filePath) continue;
            try {
                await fetch('/delete', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: JSON.stringify({ path: old.filePath }) });
            } catch (e) { console.warn('Payroll: old file not deleted', e); }
        }
        // The replacement keeps the record's id, so the server updates it instead of refusing a duplicate.
        // Its settlement (finiquito), if any, stays attached.
        const fresh = items.map(item => {
            const old = replaced.find(o => sameSlot(o, item));
            if (!old) return item;
            const settlement = Object.fromEntries(this.SETTLEMENT_FIELDS.filter(key => old[key] != null).map(key => [key, old[key]]));
            return { ...settlement, ...item, ...(old.id ? { id: old.id } : {}) };
        });
        FileStorage.write(this.HISTORY_KEY, [...fresh.slice().reverse(), ...history.filter(old => !replaced.includes(old))]);
    },

    notifyUploaded(professorId, monthName, year, settlement = false, invoice = false) {
        const user = (AuthManager.users || []).find(u => u.professorId === professorId);
        if (user) DataManager.addNotification({ receiverId: user.username, title: settlement ? 'Nuevo Finiquito Disponible' : invoice ? 'Factura Registrada' : 'Nueva Nómina Disponible',
            message: settlement ? `Se ha subido tu finiquito de ${monthName} de ${year}. Lo tienes junto a tu nómina de ese mes, en tu sección Laboral.`
                : invoice ? `Se ha registrado tu factura de ${monthName} de ${year}. Puedes verla en tu sección Laboral.`
                : `Se ha subido tu nómina correspondiente a ${monthName} de ${year}. Puedes verla en tu sección Laboral.`,
            type: 'info', icon: settlement ? 'fas fa-file-signature' : 'fas fa-file-invoice-dollar', targetView: 'nominas' });
    },

    /**
     * Attaches settlements (finiquitos) to the payslips of their month in one write. One per
     * person and month: a new one replaces the earlier one, whose file is deleted first.
     */
    async saveSettlements(items) {
        const history = this.getHistory();
        const slot = item => history.find(h => h.professorId === item.professorId && Number(h.monthIndex) === Number(item.monthIndex) && String(h.year) === String(item.year));
        for (const item of items) {
            const old = slot(item)?.settlementFilePath;
            if (!old) continue;
            try {
                await fetch('/delete', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: JSON.stringify({ path: old }) });
            } catch (e) { console.warn('Payroll: old settlement not deleted', e); }
        }
        const pick = item => Object.fromEntries(this.SETTLEMENT_FIELDS.map(key => [key, item[key] ?? null]));
        FileStorage.write(this.HISTORY_KEY, history.map(h => { const item = items.find(i => slot(i) === h); return item ? { ...h, ...pick(item) } : h; }));
    },

    deleteSettlement(timestamp) {
        if (!AuthManager.requireModuleManage("nominas",["admin","rrhh"])) return;
        const item = this.getHistory().find(h => h.timestamp === timestamp);
        if (!item?.settlementFilePath) return;
        Utils.showConfirm(`¿Quitar el finiquito de ${item.professorName} de ${item.month} ${item.year}? Su nómina se queda; el archivo del finiquito se borra.`, async () => {
            try {
                const response = await fetch('/delete', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: JSON.stringify({ path: item.settlementFilePath }) });
                const result = await response.json().catch(() => null);
                if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo borrar el archivo');
                FileStorage.write(this.HISTORY_KEY, this.getHistory().map(h => h.timestamp === timestamp ? { ...h, ...Object.fromEntries(this.SETTLEMENT_FIELDS.map(key => [key, null])) } : h));
                ModalManager.closeModal();
                Utils.showToast('Finiquito quitado.', 'success');
                this.renderSection();
            } catch (error) {
                console.error('Settlement delete:', error);
                Utils.showToast('No se pudo quitar el finiquito.', 'error');
            }
        });
    },

    getHistory() {
        if (window.FileStorage) {
            const data = FileStorage.read(this.HISTORY_KEY);
            return data || [];
        }
        const data = BrowserState.getItem(this.HISTORY_KEY);
        return data ? JSON.parse(data) : [];
    },

    /** Saves new amounts on some payslips of the history (by upload time) in one write. */
    saveAmounts(changes) {
        const history = this.getHistory().map(item => changes.has(item.timestamp) ? { ...item, ...changes.get(item.timestamp) } : item);
        FileStorage.write(this.HISTORY_KEY, history);
    },

    editAmounts(timestamp) {
        if (!AuthManager.requireModuleManage("nominas",["admin","rrhh"])) return;
        const item = this.getHistory().find(h => h.timestamp === timestamp);
        if (!item) return;
        const keys = ['companyCost', 'grossPay', 'netPay', ...(item.settlementFilePath ? ['settlementCost', 'settlementNet'] : [])];
        const field = (key, label) => `<div class="form-group">
                <label class="form-label" for="pa-${key}">${label}</label>
                <input type="number" class="form-input" id="pa-${key}" name="${key}" min="0" step="0.01" inputmode="decimal" value="${NominasBatch.amountValue(item[key])}">
            </div>`;
        ModalManager.show(`Importes · ${item.professorName} · ${item.month} ${item.year}`, `
            <div class="dnr-proposal professor-form-dnr">
            <form id="payroll-amounts-form" class="form-container">
                <p class="pf-hint">${item.isInvoice ? 'Factura de autónomo: su total es lo que cuenta en «Gastos del curso».' : 'El coste empresa es lo que cuenta en «Gastos del curso». Están en la propia nómina.'}</p>
                <div class="form-grid">
                    ${field('companyCost', item.isInvoice ? 'Total de la factura (€)' : 'Coste empresa (€)')}
                    ${field('grossPay', item.isInvoice ? 'Base imponible (€)' : 'Bruto (€)')}
                    ${field('netPay', item.isInvoice ? 'A pagar, sin la retención (€)' : 'Líquido (€)')}
                    ${item.settlementFilePath ? field('settlementCost', 'Finiquito: Suma (€)') + field('settlementNet', 'Finiquito: líquido (€)') : ''}
                </div>
                <div class="form-actions">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){ModalManager.closeModal();}, [])}>Cancelar</button>
                    <button type="submit" class="btn btn-primary"><i class="fas fa-save"></i> Guardar</button>
                </div>
            </form>
            </div>`);
        document.getElementById('payroll-amounts-form').addEventListener('submit', event => {
            event.preventDefault();
            const form = event.target;
            this.saveAmounts(new Map([[timestamp, Object.fromEntries(keys.map(key => [key, NominasBatch.parseAmount(form.elements[key].value)]))]]));
            ModalManager.closeModal();
            Utils.showToast('Importes guardados.', 'success');
            this.renderSection();
        });
    },

    /** Reads the amounts of this month's PDFs saved before they were read on upload. */
    async readStoredAmounts(button) {
        if (!AuthManager.requireModuleManage("nominas",["admin","rrhh"]) || button.disabled) return;
        button.disabled = true;
        const { month, year } = this.selectedUploadMonth();
        const items = this.payslipsOf(month, year).filter(item => item.companyCost == null && item.filePath && String(item.fileType).includes('pdf'));
        const changes = new Map();
        let failed = 0;
        for (const [index, item] of items.entries()) {
            button.textContent = `Leyendo ${index + 1} de ${items.length}…`;
            try {
                const { blob } = await PrivateDownloads.fetchFile('/' + item.filePath.replace(/^\//, ''));
                const amounts = await NominasBatch.readPdfAmounts(new Uint8Array(await blob.arrayBuffer()));
                if (amounts.companyCost == null) failed++;
                const found = Object.fromEntries(Object.entries(amounts).filter(([, value]) => value != null));
                if (Object.keys(found).length) changes.set(item.timestamp, found);
            } catch (e) {
                failed++;
            }
        }
        if (changes.size) this.saveAmounts(changes);
        const read = items.length - failed;
        Utils.showToast(failed ? `Leídas ${read} de ${items.length}. Las demás no muestran el coste empresa: escríbelo con el lápiz.` : `Importes leídos en ${read} ${read === 1 ? 'nómina' : 'nóminas'}.`, failed ? 'warning' : 'success');
        this.renderSection();
    },

    deleteDocument(timestamp) {
        if (!AuthManager.requireModuleManage("nominas",["admin","rrhh"])) return;
        const history = this.getHistory();
        const item = history.find(h => h.timestamp === timestamp);

        if (!item) return;

        Utils.showConfirm(`¿Estás seguro de que quieres eliminar la nómina de ${item.professorName}?${item.settlementFilePath ? ' Su finiquito de ese mes también se borra.' : ''} Esta acción borrará el archivo permanentemente.`, async () => {
            try {
                // 1. Delete file from server (if applicable)
                if (item.filePath) {
                    const token = AuthManager.getCsrfToken();
                    const response = await fetch('/delete', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-SICAF-CSRF': token
                        },
                        body: JSON.stringify({ path: item.filePath })
                    });
                    const result = await response.json();
                    if (!response.ok || !result.success) {
                        throw new Error(result.error || 'No se pudo eliminar el archivo');
                    }
                }
                // Its settlement goes with it (while the record still exists, the server allows it).
                if (item.settlementFilePath) {
                    await fetch('/delete', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: JSON.stringify({ path: item.settlementFilePath }) })
                        .catch(e => console.warn('Payroll: settlement file not deleted', e));
                }

                // 2. Update local history
                const newHistory = history.filter(h => h.timestamp !== timestamp);

                if (window.FileStorage) {
                    FileStorage.write(this.HISTORY_KEY, newHistory);
                } else {
                    BrowserState.setItem(this.HISTORY_KEY, JSON.stringify(newHistory));
                }

                // 3. Update UI
                ModalManager.closeModal();
                Utils.showToast('Nómina eliminada correctamente', 'success');

                // Refresh whichever section is currently visible
                this.renderSection();

            } catch (error) {
                console.error('Delete error:', error);
                Utils.showToast('Error al eliminar el documento', 'error');
            }
        });
    },

    /** The settlement (finiquito) of a payslip, shaped like a document of its own. */
    settlementDocument(item) {
        return item?.settlementFilePath ? { ...item, filePath: item.settlementFilePath, fileName: item.settlementFileName || 'Finiquito.pdf', fileType: 'application/pdf', fileData: null, label: 'Finiquito' } : null;
    },

    async viewDocument(timestamp, settlement = false) {
        const history = this.getHistory();
        const found = history.find(h => h.timestamp === timestamp);
        const item = settlement ? this.settlementDocument(found) : found;
        if (item) {
            // A payslip with a settlement opens with the settlement's pages after it.
            const joined = !settlement && await NominasBatch.payslipWithSettlement(item).catch(e => { console.warn('Payslip + settlement:', e); return null; });
            if (joined) {
                if (this._privatePreviewUrl) URL.revokeObjectURL(this._privatePreviewUrl);
                this._privatePreviewUrl = joined;
                this.openModal({ ...item, fileData: joined, fileType: 'application/pdf', fileName: NominasBatch.joinedName(item), label: 'Nómina y finiquito' });
            } else if (item.filePath) {
                await PrivateDownloads.preview('/' + item.filePath.replace(/^\//, ''), url => {
                    if (this._privatePreviewUrl) URL.revokeObjectURL(this._privatePreviewUrl);
                    this._privatePreviewUrl = url;
                    this.openModal({ ...item, fileData: url });
                });
            } else if (item.fileData) {
                this.openModal(item);
            } else {
                Utils.showToast('Archivo no disponible', 'error');
            }
        } else {
            Utils.showToast('Registro no encontrado', 'error');
        }
    },

    async downloadDocument(timestamp, settlement = false) {
        const history = this.getHistory();
        const found = history.find(h => h.timestamp === timestamp);
        const item = settlement ? this.settlementDocument(found) : found;
        if (item) {
            const joined = !settlement && await NominasBatch.payslipWithSettlement(item).catch(() => null);
            if (joined) {
                this._triggerDownload(joined, NominasBatch.joinedName(item));
                setTimeout(() => URL.revokeObjectURL(joined), 60000);
            } else if (item.filePath) {
                PrivateDownloads.download('/' + item.filePath.replace(/^\//, ''));
            } else if (item.fileData) {
                this._triggerDownload(item.fileData, item.fileName);
            } else {
                Utils.showToast('Archivo no disponible', 'error');
            }
        } else {
            Utils.showToast('Registro no encontrado', 'error');
        }
    },

    _triggerDownload(url, name) {
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        link.target = '_blank'; // Open in new tab if download fails
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        Utils.showToast('Descarga iniciada', 'success');
    },

    openModal(item) {
        ModalManager.closeModal();
        const modal = document.getElementById('payroll-modal');
        // Inside the view a parent with visual effects turns «fixed» into «relative to the view»,
        // so a viewer opened low in the page showed up at its top. It lives on <body> while open
        // (an older copy left there by a previous visit goes away).
        document.querySelectorAll('body > #payroll-modal').forEach(old => { if (old !== modal) old.remove(); });
        if (modal.parentElement !== document.body) document.body.append(modal);
        const title = document.getElementById('modal-doc-title');
        const content = document.getElementById('modal-doc-content');
        const downloadBtn = document.getElementById('modal-download-btn');

        title.textContent = `${item.label ? item.label + ' · ' : ''}${item.professorName} - ${item.month} ${item.year}`;

        let previewUrl;
        try { previewUrl = PrivateDownloads.renderInlinePreview(content, item.fileData, item.fileType); }
        catch (error) { Utils.showToast(error.message, 'error'); return; }
        downloadBtn.onclick = () => this._triggerDownload(previewUrl, item.fileName);

        modal.style.display = 'flex';
        setTimeout(() => {
            modal.style.opacity = '1';
            modal.querySelector('.modal-container').style.transform = 'scale(1)';
        }, 10);
    },

    closeModal() {
        PrivateDownloads.clearInlinePreview(document.getElementById('modal-doc-content'));
        if (this._privatePreviewUrl) URL.revokeObjectURL(this._privatePreviewUrl);
        this._privatePreviewUrl = null;
        const modal = document.getElementById('payroll-modal');
        modal.style.opacity = '0';
        modal.querySelector('.modal-container').style.transform = 'scale(0.9)';
        setTimeout(() => {
            modal.style.display = 'none';
            SafeHTML.set(document.getElementById('modal-doc-content'), '');
        }, 300);
    },

    // ========================================
    // SECCIÓN: Listado por Mes (el curso por persona)
    // ========================================

    /** Courses with payslips, plus the current one, newest first. */
    payrollCourses() {
        const courses = new Set([this.currentCourse()]);
        for (const h of this.getHistory()) {
            const course = this.courseOf(Number(h.monthIndex), Number(h.year));
            if (Number.isFinite(course)) courses.add(course);
        }
        return [...courses].sort((a, b) => b - a);
    },

    renderListadoSectionHTML() {
        if (this.listadoYear === null) this.listadoYear = this.currentCourse();
        const course = this.listadoYear;
        this.needCourse(course, () => { if (this.activeSection === 'listado') this.renderSection(); });
        const segment = (value, label) => `<button type="button" class="${this.listadoPay === value ? 'is-on' : ''}" aria-pressed="${this.listadoPay === value}" ${UIEvents.attr("click", function(event,captured){NominasUpload.listadoPay=String(captured[0]);NominasUpload.renderSection();}, [value])}>${label}</button>`;
        return `
            <div class="pn-stack">
                <section class="pn-card pn-toolbar">
                    <label class="pn-search">
                        <i class="fas fa-search" aria-hidden="true"></i>
                        <input type="search" placeholder="Buscar persona" aria-label="Buscar persona" value="${Utils.sanitizeHTML(this.listadoSearch)}" ${UIEvents.attr("input", function(event,captured){NominasUpload.listadoSearch=this.value;NominasUpload.renderListadoTable();}, [])}>
                    </label>
                    <select class="pn-select" aria-label="Curso" ${UIEvents.attr("change", function(event,captured){NominasUpload.listadoYear=Number(this.value);NominasUpload.renderSection();}, [])}>
                        ${this.payrollCourses().map(y => `<option value="${y}" ${y === course ? 'selected' : ''}>Curso ${y} / ${y + 1}</option>`).join('')}
                    </select>
                    <div class="pn-segment" role="group" aria-label="Tipo de pago">
                        ${segment('all', 'Todos')}${segment('hourly', 'Por horas')}${segment('fixed', 'Sueldo fijo')}
                    </div>
                    <label class="pn-check"><input type="checkbox" ${this.listadoOnlyMissing ? 'checked' : ''} ${UIEvents.attr("change", function(event,captured){NominasUpload.listadoOnlyMissing=this.checked;NominasUpload.renderListadoTable();}, [])}> Solo con nóminas que faltan</label>
                    <span class="pn-grow"></span>
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){NominasUpload.exportListadoExcel();}, [])}>
                        <i class="fas fa-file-excel"></i> Exportar a Excel
                    </button>
                </section>
                <div class="pn-legend">
                    <span><span class="pn-swatch is-ok"></span>Subida, con su coste empresa</span>
                    <span><span class="pn-swatch is-missing"></span>Subida sin importe</span>
                    <span><span class="pn-swatch is-due"></span>Falta (tenía clases o contrato)</span>
                    <span><span class="pn-swatch is-off"></span>No le tocaba</span>
                    <span><span class="pn-swatch is-ok has-settlement"></span>Con finiquito (sumado)</span>
                </div>
                <section class="pn-card pn-table-card" aria-label="Nóminas del curso por persona">
                    <div class="pn-scroll" id="pn-listado-table">${this.listadoTableHTML()}</div>
                </section>
                <p class="pn-hint">${this.canManage() ? 'Pulsa una casilla para ver la nómina, cambiar sus importes o subir la que falta.' : 'Pulsa una casilla para ver la nómina.'}</p>
            </div>
        `;
    },

    renderListadoTable() {
        const box = document.getElementById('pn-listado-table');
        if (box) SafeHTML.set(box, this.listadoTableHTML());
    },

    listadoTableHTML() {
        const course = this.listadoYear, months = this.courseMonths(course), history = this.getHistory();
        const manage = this.canManage();
        const byMonth = months.map(({ month, year }) => {
            return { month, year, due: this.isDue(month, year) ? this.dueIds(month, year) : new Set(), payslips: new Map(this.payslipsOf(month, year, history).map(h => [h.professorId, h])) };
        });
        const ids = new Set();
        byMonth.forEach(m => { m.due.forEach(id => ids.add(id)); m.payslips.forEach((h, id) => ids.add(id)); });
        const term = this.normalizeText(this.listadoSearch);
        const people = [...ids].map(id => {
            const fallback = byMonth.map(m => m.payslips.get(id)).find(Boolean)?.professorName || 'Desconocido';
            const professor = DataManager.getProfessor(id);
            let done = 0, missing = 0;
            const cells = byMonth.map(m => {
                const name = `${this.MONTH_NAMES[m.month - 1]} ${m.year}`;
                const h = m.payslips.get(id);
                if (h) {
                    done++;
                    const cost = this.totalOf(h, 'companyCost'), settled = h.settlementFilePath ? ' (con finiquito)' : '';
                    return cost == null
                        ? { cls: 'is-missing', text: 'Sin €', aria: `${name}: subida sin importe${settled}`, open: h.timestamp, settled: !!settled }
                        : { cls: 'is-ok', text: `${Math.round(cost).toLocaleString('es-ES')} €`, aria: `${name}: ${this.money(cost)}${settled}`, open: h.timestamp, settled: !!settled };
                }
                if (m.due.has(id)) { missing++; return { cls: 'is-due', text: 'Falta', aria: `${name}: falta la nómina`, upload: [m.month, m.year] }; }
                return { cls: 'is-off', text: '—', aria: `${name}: no le tocaba` };
            });
            return { id, name: this.personName(id, fallback), kind: this.payLabel(id), fixed: professor ? StaffPay.isFixed(professor) : false, cells, done, missing };
        }).filter(p => (!term || this.normalizeText(p.name).includes(term))
            && (this.listadoPay === 'all' || (this.listadoPay === 'fixed') === p.fixed)
            && (!this.listadoOnlyMissing || p.missing > 0))
            .sort((a, b) => a.name.localeCompare(b.name));

        if (!people.length) {
            return `<div class="dnr-empty pn-empty">${ids.size ? 'Nadie coincide con los filtros.' : `Aún no hay nóminas ni clases en el curso ${course} / ${course + 1}.`}</div>`;
        }
        const cell = (c, person) => {
            if (c.open) return `<button type="button" class="pn-cell ${c.cls} ${c.settled ? 'has-settlement' : ''}" aria-label="${Utils.sanitizeHTML(`${person.name}, ${c.aria}`)}" ${UIEvents.attr("click", function(event,captured){NominasUpload.openPayslip(String(captured[0]));}, [c.open])}>${c.text}</button>`;
            if (c.upload && manage) return `<button type="button" class="pn-cell ${c.cls}" aria-label="${Utils.sanitizeHTML(`${person.name}, ${c.aria}: subirla`)}" ${UIEvents.attr("click", function(event,captured){NominasUpload.openManualUpload(String(captured[0]),Number(captured[1]),Number(captured[2]));}, [person.id, c.upload[0], c.upload[1]])}>${c.text}</button>`;
            return `<span class="pn-cell ${c.cls}" aria-label="${Utils.sanitizeHTML(`${person.name}, ${c.aria}`)}">${c.text}</span>`;
        };
        return `<table class="pn-table pn-matrix">
            <thead><tr>
                <th scope="col" class="pn-sticky">Persona</th>
                ${months.map(m => `<th scope="col" class="pn-center">${this.MONTH_NAMES[m.month - 1].slice(0, 3)}</th>`).join('')}
                <th scope="col" class="pn-num pn-total-col">Subidas</th>
            </tr></thead>
            <tbody>
                ${people.map(p => `<tr>
                    <th scope="row" class="pn-sticky"><span class="pn-person-text">${this.personNameHTML(p.id, p.name)}<span class="pn-person-kind">${Utils.sanitizeHTML(p.kind)}</span></span></th>
                    ${p.cells.map(c => `<td class="pn-cell-td">${cell(c, p)}</td>`).join('')}
                    <td class="pn-num pn-total-col ${p.missing ? 'is-due' : 'is-done'}">${p.done} / ${p.done + p.missing}</td>
                </tr>`).join('')}
            </tbody>
        </table>`;
    },

    /** One payslip from the course grid: its amounts and what can be done with it. */
    openPayslip(timestamp) {
        const h = this.getHistory().find(item => item.timestamp === timestamp);
        if (!h) return;
        const manage = this.canManage();
        const amount = value => value == null ? '—' : this.money(value);
        ModalManager.show(`${this.personName(h.professorId, h.professorName)} · ${h.month} ${h.year}`, `
            <div class="dnr-proposal professor-form-dnr">
                <dl class="pn-amounts-list">
                    <div><dt>Coste empresa</dt><dd>${h.companyCost == null ? '<span class="pn-missing">Sin importe</span>' : amount(h.companyCost)}</dd></div>
                    <div><dt>Bruto</dt><dd>${amount(h.grossPay)}</dd></div>
                    <div><dt>Líquido</dt><dd>${amount(h.netPay)}</dd></div>
                    ${h.settlementFilePath ? `<div><dt>Finiquito · Suma</dt><dd>${amount(h.settlementCost)}</dd></div>
                    <div><dt>Finiquito · líquido</dt><dd>${amount(h.settlementNet)}</dd></div>
                    <div><dt>Fecha de cese</dt><dd>${h.settlementDate ? Utils.sanitizeHTML(new Date(`${h.settlementDate}T12:00:00`).toLocaleDateString('es-ES')) : '—'}</dd></div>` : ''}
                </dl>
                <div class="form-actions">
                    ${manage ? `<button type="button" class="btn btn-secondary pn-danger-text" ${UIEvents.attr("click", function(event,captured){NominasUpload.deleteDocument(String(captured[0]));}, [timestamp])}><i class="fas fa-trash-alt"></i> Eliminar</button>` : ''}
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){NominasUpload.downloadDocument(String(captured[0]));}, [timestamp])}><i class="fas fa-download"></i> Descargar</button>
                    ${manage ? `<button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){NominasUpload.editAmounts(String(captured[0]));}, [timestamp])}><i class="fas fa-pen"></i> Importes</button>` : ''}
                    ${h.settlementFilePath ? `<button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){NominasUpload.viewDocument(String(captured[0]),true);}, [timestamp])}><i class="fas fa-file-signature"></i> Ver finiquito</button>` : ''}
                    <button type="button" class="btn btn-primary" ${UIEvents.attr("click", function(event,captured){NominasUpload.viewDocument(String(captured[0]));}, [timestamp])}><i class="fas fa-eye"></i> Ver</button>
                </div>
            </div>`);
    },

    exportListadoExcel() {
        // Through fetch with the tab key: a plain link cannot send it (shared-device sessions).
        PrivateDownloads.download('/api/payroll/list/export');
    },

    // ========================================
    // SECCIÓN: Gastos del Curso
    // ========================================

    // Cursos lectivos (Sep-Ago) para los que existe al menos una clase programada,
    // opcionalmente restringido a un conjunto de formaciones (formationIds)
    getAcademicYearsWithData(formationIds) {
        // Payslips cost money even with no classes programmed: across all formations every course
        // with an uploaded payslip has data, and so does the current one when someone has a fixed salary.
        const allFormations = !(formationIds && formationIds.length);
        const current = this.currentCourse();
        const withFixed = years => {
            if (!allFormations) return years;
            const all = new Set(years);
            for (const h of this.getHistory()) all.add(this.courseOf(Number(h.monthIndex), Number(h.year)));
            if ((DataManager.getProfessors() || []).some(p => StaffPay.isFixed(p))) all.add(current);
            return [...all].filter(Number.isFinite);
        };
        // The server knows which courses have classes for these formations; costs are computed there too.
        if (typeof FileStorage !== 'undefined' && FileStorage.needClassYears) {
            const years = FileStorage.needClassYears(allFormations ? null : formationIds, () => this.renderSection());
            if (years) return withFixed([...years]).sort((a, b) => b - a);
        }
        const years = new Set();
        (DataManager.getClasses() || []).forEach(c => {
            if (!c.startDate) return;
            if (formationIds && formationIds.length > 0 && !formationIds.includes(c.formationId)) return;
            const d = new Date(c.startDate);
            years.add(this.courseOf(d.getMonth() + 1, d.getFullYear()));
        });
        return withFixed([...years]).sort((a, b) => b - a);
    },

    normalizeText(str) {
        return String(str || '')
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            .trim();
    },

    // Quita el sufijo de curso ("1º", "2º A", "2ºB"...) del nombre de una formación,
    // para poder agrupar "Dirección 1º", "Direccion 2º" y "Dirección 3º" en un mismo programa
    stripOrdinalSuffix(name) {
        return String(name || '').replace(/\s+\d+\s*º\s*[A-Za-z]?\.?\s*$/i, '').trim();
    },

    // Agrupa todas las formaciones por su nombre base (sin el sufijo de curso),
    // p.ej. "Dirección" -> [Dirección 1º, Direccion 2º, Dirección 3º]
    getFormationGroups() {
        const formations = DataManager.getFormations();
        const groups = new Map();

        formations.forEach(f => {
            const baseName = this.stripOrdinalSuffix(f.name) || f.name;
            const key = this.normalizeText(baseName);
            if (!groups.has(key)) {
                groups.set(key, { key, label: baseName, formationIds: [], members: [] });
            }
            const group = groups.get(key);
            group.formationIds.push(f.id);
            group.members.push(f.name);
        });

        return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
    },

    // Devuelve { formationIds, label } según el ámbito seleccionado (Todas / Formación / Programa)
    getCurrentCostScopeFilter() {
        if (this.costScope === 'formation' && this.costFormationId) {
            const f = DataManager.getFormation(this.costFormationId);
            return { formationIds: [this.costFormationId], label: f ? f.name : 'Formación' };
        }
        if (this.costScope === 'program' && this.costProgramKey) {
            const group = this.getFormationGroups().find(g => g.key === this.costProgramKey);
            if (group) return { formationIds: group.formationIds, label: group.label };
        }
        return { formationIds: null, label: 'Toda la escuela' };
    },

    renderGastosSectionHTML() {
        const allYears = this.getAcademicYearsWithData();

        if (allYears.length === 0) {
            return `
                <div class="dnr-empty" style="padding: 3rem 1rem;">
                    <i class="fas fa-chart-line" style="font-size: 1.5rem; display: block; margin-bottom: 0.75rem; opacity: 0.6;"></i>
                    Aún no hay nóminas ni clases en ningún curso, así que no hay gastos que mostrar.
                </div>
            `;
        }

        const formations = DataManager.getFormations().slice().sort((a, b) => a.name.localeCompare(b.name));
        const programGroups = this.getFormationGroups();

        if (this.costScope === 'formation' && !this.costFormationId) {
            this.costFormationId = formations[0] ? formations[0].id : null;
        }
        if (this.costScope === 'program' && !this.costProgramKey) {
            this.costProgramKey = programGroups[0] ? programGroups[0].key : null;
        }

        const scope = this.getCurrentCostScopeFilter();
        const years = this.getAcademicYearsWithData(scope.formationIds);

        if (!this.costYearStart || !years.includes(this.costYearStart)) {
            this.costYearStart = years[0] || allYears[0];
        }
        const segment = (value, label) => `<button type="button" class="${this.costScope === value ? 'is-on' : ''}" aria-pressed="${this.costScope === value}" ${UIEvents.attr("click", function(event,captured){NominasUpload.changeCostScope(String(captured[0]));}, [value])}>${label}</button>`;
        const group = this.costScope === 'program' ? programGroups.find(p => p.key === this.costProgramKey) : null;

        return `
            <div class="pn-stack">
                <section class="pn-card pn-toolbar">
                    <div class="pn-segment" role="group" aria-label="Ámbito">
                        ${segment('all', 'Toda la escuela')}${segment('formation', 'Una formación')}${segment('program', 'Un programa')}
                    </div>
                    ${this.costScope === 'formation' ? `
                        <select class="pn-select" aria-label="Formación" ${UIEvents.attr("change", function(event,captured){NominasUpload.changeCostFormationScope(this.value);}, [])}>
                            ${formations.map(f => `<option value="${Utils.sanitizeHTML(f.id)}" ${this.costFormationId === f.id ? 'selected' : ''}>${Utils.sanitizeHTML(f.name)}</option>`).join('')}
                        </select>` : ''}
                    ${this.costScope === 'program' ? `
                        <select class="pn-select" aria-label="Programa" ${UIEvents.attr("change", function(event,captured){NominasUpload.changeCostProgramScope(this.value);}, [])}>
                            ${programGroups.map(g => `<option value="${Utils.sanitizeHTML(g.key)}" ${this.costProgramKey === g.key ? 'selected' : ''}>${Utils.sanitizeHTML(g.label)} ${g.members.length > 1 ? `(${g.members.length} cursos)` : ''}</option>`).join('')}
                        </select>` : ''}
                    <select class="pn-select" aria-label="Curso" ${UIEvents.attr("change", function(event,captured){NominasUpload.changeCostYear(this.value);}, [])}>
                        ${years.map(y => `<option value="${y}" ${y === this.costYearStart ? 'selected' : ''}>Curso ${y} / ${y + 1}</option>`).join('')}
                    </select>
                    <span class="pn-grow"></span>
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){NominasUpload.exportGastosExcel();}, [])}>
                        <i class="fas fa-file-excel"></i> Exportar a Excel
                    </button>
                </section>
                <p class="pn-hint pn-scope-note">${this.costScope === 'all'
                    ? 'Coste empresa real, leído de las nóminas subidas. Los meses sin nómina salen con «—».'
                    : 'Coste empresa real de las nóminas. Por horas: según las horas que dio cada profesor aquí ese mes. Sueldo fijo: la parte de su jornada que dio clase aquí, más su parte del resto repartido entre todas las formaciones por sus horas de clase del mes (los meses sin clases, a partes iguales).'}
                    ${group && group.members.length > 1 ? ` Incluye: ${Utils.sanitizeHTML(group.members.join(', '))}.` : ''}</p>
                <div id="gastos-summary-cards" class="pn-stats"></div>
                <section class="pn-card pn-pad pn-chart-card" aria-labelledby="pn-chart-title" id="gastos-chart"></section>
                <section class="pn-card pn-table-card" aria-labelledby="pn-costs-title">
                    <div class="pn-table-head">
                        <h2 class="pn-title" id="pn-costs-title">Por persona</h2>
                        <span class="pn-hint">Coste empresa real de cada nómina. «—»: sin nómina ese mes.</span>
                    </div>
                    <div class="pn-scroll" id="gastos-table-wrapper"></div>
                </section>
            </div>
        `;
    },

    changeCostScope(value) {
        this.costScope = value;
        this.costData = null;
        this.renderSection();
    },

    changeCostFormationScope(value) {
        this.costFormationId = value;
        this.costData = null;
        this.renderSection();
    },

    changeCostProgramScope(value) {
        this.costProgramKey = value;
        this.costData = null;
        this.renderSection();
    },

    changeCostYear(value) {
        this.costYearStart = parseInt(value, 10);
        this.costData = null;
        this.loadAndRenderCosts();
    },

    async loadAndRenderCosts() {
        const wrapper = document.getElementById('gastos-table-wrapper');
        const summary = document.getElementById('gastos-summary-cards');
        const chart = document.getElementById('gastos-chart');
        if (!wrapper) return;

        SafeHTML.set(wrapper, `<div class="pn-loading"><i class="fas fa-spinner fa-spin"></i> Calculando gastos del curso...</div>`);
        if (summary) SafeHTML.set(summary, '');
        if (chart) SafeHTML.set(chart, '');

        try {
            const scope = this.getCurrentCostScopeFilter();
            const params = new URLSearchParams({ year: this.costYearStart });
            if (scope.formationIds) params.set('formationIds', scope.formationIds.join(','));

            const response = await fetch(`/api/payroll/costs?${params.toString()}`, {
                headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() }
            });
            const result = await response.json();
            if (!result.success) throw new Error(result.error || 'Error al cargar los gastos');

            this.costData = result;
            this.renderGastosSummary(result);
            this.renderGastosChart(result);
            this.renderGastosTable(result, scope);
        } catch (error) {
            console.error('Error loading payroll costs:', error);
            SafeHTML.set(wrapper, `<div class="dnr-empty pn-empty">No se pudieron calcular los gastos del curso.</div>`);
        }
    },

    renderGastosSummary(result) {
        const summary = document.getElementById('gastos-summary-cards');
        if (!summary) return;

        const { rows, months } = result;
        const totalCost = rows.reduce((s, r) => s + r.totalCost, 0);
        const totalHours = rows.reduce((s, r) => s + r.totalHours, 0);
        const cells = rows.flatMap(r => r.monthCosts);
        const real = cells.filter(mc => mc.payslip === 'real').length, missing = cells.filter(mc => mc.payslip === 'missing').length;
        // Last month with a real cost: «hasta septiembre».
        const lastIndex = months.reduce((last, mo, i) => rows.some(r => r.monthCosts[i].payslip === 'real') ? i : last, -1);
        const monthCost = i => rows.reduce((s, r) => s + r.monthCosts[i].cost, 0);

        // The last month with payslips, against the one before (or the course's monthly average).
        const lastMonthCard = () => {
            if (lastIndex < 0) return { label: 'Último mes', value: '—', sub: 'Aún sin nóminas con importe' };
            const last = monthCost(lastIndex);
            const previous = lastIndex > 0 && rows.some(r => r.monthCosts[lastIndex - 1].payslip === 'real') ? monthCost(lastIndex - 1) : null;
            const withCost = months.filter((mo, i) => rows.some(r => r.monthCosts[i].payslip === 'real')).length;
            const diff = previous ? Math.round((last - previous) / previous * 100) : null;
            return {
                label: `Último mes · ${months[lastIndex].label}`, value: this.money(last),
                sub: diff !== null ? `${diff > 0 ? '+' : ''}${diff} % frente a ${months[lastIndex - 1].label.toLowerCase()}` : withCost > 1 ? `Media mensual ${this.money(totalCost / withCost)}` : 'Primer mes con nóminas'
            };
        };

        const cards = [
            { label: 'Coste empresa del curso', value: this.money(totalCost), sub: lastIndex >= 0 ? `Hasta ${months[lastIndex].label.toLowerCase()}` : 'Aún sin nóminas con importe' },
            { label: 'Horas impartidas', value: `${Math.round(totalHours).toLocaleString('es-ES')} horas`, sub: this.costScope === 'all' ? 'En todas las formaciones' : 'En este ámbito' },
            { label: 'Nóminas con importe', value: String(real), sub: missing ? `${missing} sin coste empresa` : 'Todas con coste empresa', warn: !!missing },
            lastMonthCard()
        ];

        SafeHTML.set(summary, cards.map(c => `<div class="pn-stat">
            <span class="pn-stat-label">${Utils.sanitizeHTML(c.label)}</span>
            <span class="pn-stat-value" title="${Utils.sanitizeHTML(c.value)}">${Utils.sanitizeHTML(c.value)}</span>
            <span class="pn-stat-sub ${c.warn ? 'is-warn' : ''}">${Utils.sanitizeHTML(c.sub)}</span>
        </div>`).join(''));
    },

    /** Coste empresa of each month, split into fixed salaries and hourly pay. */
    renderGastosChart(result) {
        const chart = document.getElementById('gastos-chart');
        if (!chart) return;
        const { rows, months } = result;
        const split = months.map((mo, i) => rows.reduce((s, r) => {
            s[r.payType === 'fixed' ? 'fixed' : 'hourly'] += r.monthCosts[i].cost;
            return s;
        }, { fixed: 0, hourly: 0 }));
        const max = Math.max(0, ...split.map(s => s.fixed + s.hourly));
        const short = n => n >= 1000 ? `${(n / 1000).toLocaleString('es-ES', { maximumFractionDigits: 1 })}k` : Math.round(n).toLocaleString('es-ES');
        const height = n => max ? Math.round(n / max * 150) : 0;
        SafeHTML.set(chart, `
            <div class="pn-row-between">
                <h2 class="pn-title" id="pn-chart-title">Coste empresa por mes</h2>
                <div class="pn-legend">
                    <span><span class="pn-swatch is-fixed"></span>Sueldos fijos</span>
                    <span><span class="pn-swatch is-hourly"></span>Por horas</span>
                </div>
            </div>
            ${max ? `<div class="pn-bars" role="img" aria-label="Coste empresa de cada mes del curso: ${months.map((mo, i) => `${mo.label} ${this.money(split[i].fixed + split[i].hourly)}`).join(', ')}">
                ${split.map(s => `<div class="pn-bar-col">
                    <span class="pn-bar-value">${s.fixed + s.hourly > 0 ? short(s.fixed + s.hourly) : ''}</span>
                    <span class="pn-bar-stack">
                        <span class="pn-bar is-hourly" style="height: ${height(s.hourly)}px"></span>
                        <span class="pn-bar is-fixed" style="height: ${height(s.fixed)}px"></span>
                    </span>
                </div>`).join('')}
            </div>
            <div class="pn-bar-labels">${months.map(mo => `<span>${mo.label.slice(0, 3)}</span>`).join('')}</div>`
            : '<div class="dnr-empty pn-empty">Aún no hay nóminas con coste empresa en este curso.</div>'}
        `);
    },

    renderGastosTable(result, scope) {
        const wrapper = document.getElementById('gastos-table-wrapper');
        if (!wrapper) return;

        const { months, rows } = result;
        if (rows.length === 0) {
            const message = (scope && scope.formationIds)
                ? `No hay clases con coste registradas para "${Utils.sanitizeHTML(scope.label)}" en este curso lectivo.`
                : 'No hay clases ni nóminas en este curso lectivo.';
            SafeHTML.set(wrapper, `<div class="dnr-empty pn-empty">${message}</div>`);
            return;
        }

        const plain = n => n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const monthTotals = months.map((mo, i) => rows.reduce((s, r) => s + r.monthCosts[i].cost, 0));
        const grandTotal = rows.reduce((s, r) => s + r.totalCost, 0);
        // A month: its real cost, a payslip still without amount, or no payslip yet.
        const cell = mc => mc.payslip === 'real'
            ? `<td class="pn-num" title="${mc.hours > 0 ? `${Math.round(mc.hours * 10) / 10} h` : ''}">${plain(mc.cost)}</td>`
            : mc.payslip === 'missing'
                ? '<td class="pn-num" title="Nómina subida sin coste empresa: añádelo en Subir nóminas"><span class="pn-missing">Sin importe</span></td>'
                : '<td class="pn-num pn-faint">—</td>';

        SafeHTML.set(wrapper, `
            <table class="pn-table pn-matrix">
                <thead><tr>
                    <th scope="col" class="pn-sticky">Persona</th>
                    ${months.map(mo => `<th scope="col" class="pn-num">${mo.label.slice(0, 3)}</th>`).join('')}
                    <th scope="col" class="pn-num pn-total-col">Total</th>
                </tr></thead>
                <tbody>
                    ${rows.map(r => `<tr>
                        <th scope="row" class="pn-sticky"><span class="pn-person-text">${this.personNameHTML(r.professorId, r.professorName)}<span class="pn-person-kind">${Utils.sanitizeHTML(this.payLabel(r.professorId) || (r.payType === 'fixed' ? 'Sueldo fijo' : 'Por horas'))}</span></span></th>
                        ${r.monthCosts.map(cell).join('')}
                        <td class="pn-num pn-total-col pn-strong">${this.money(r.totalCost)}</td>
                    </tr>`).join('')}
                </tbody>
                <tfoot><tr>
                    <th scope="row" class="pn-sticky">Total</th>
                    ${monthTotals.map(t => `<td class="pn-num">${t > 0 ? plain(t) : '—'}</td>`).join('')}
                    <td class="pn-num pn-total-col">${this.money(grandTotal)}</td>
                </tr></tfoot>
            </table>
        `);
    },

    exportGastosExcel() {
        const scope = this.getCurrentCostScopeFilter();
        // Security scan 2026-10-01 (F6): never put the session's tab key in a URL (it stays in the
        // browser's history and downloads list); PrivateDownloads sends it as a header instead.
        const params = new URLSearchParams({ year: this.costYearStart });
        if (scope.formationIds) {
            params.set('formationIds', scope.formationIds.join(','));
            params.set('scopeLabel', scope.label);
        }
        PrivateDownloads.download(`/api/payroll/costs/export?${params.toString()}`);
    }
};

window.NominasUpload = NominasUpload;
